import { useMemo, useState } from "react"
import {
	IconCheckOutline18 as Check,
	IconChevronDownOutline18 as ChevronDown,
	IconSquaresConnectedOutline18 as SquaresConnected,
} from "nucleo-ui-outline-18"

import { Button, Popover } from "@productlane/ui"
import {
	Command,
	CommandEmpty,
	CommandInput,
	CommandItem,
	CommandList,
} from "@productlane/ui/src/components/internal/command"

import type { AgentMcpSection } from "./use-agent-mcp-sections"
import { ConnectorLogo } from "@/components/connector-logo"
import {
	mcpTriggerLabel,
	nextMcpSelection,
	resolveEnabledMcpSectionIds,
} from "./sidebar-ai-chat-mcp"
import { useSidebarAiChatState } from "./sidebar-ai-chat-state"

// Below this a search field is more chrome than help.
const SEARCH_THRESHOLD = 5

export const SidebarAiChatMcpSelector = ({
	available,
}: {
	available: AgentMcpSection[]
}) => {
	const enabledMcpSectionIds = useSidebarAiChatState(
		(s) => s.enabledMcpSectionIds,
	)
	const setEnabledMcpSectionIds = useSidebarAiChatState(
		(s) => s.setEnabledMcpSectionIds,
	)
	const [open, setOpen] = useState(false)
	const [search, setSearch] = useState("")

	const availableIds = useMemo(
		() => available.map((section) => section.id),
		[available],
	)

	const selectedIds = useMemo(
		() => resolveEnabledMcpSectionIds(enabledMcpSectionIds, availableIds),
		[enabledMcpSectionIds, availableIds],
	)

	const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])

	const selectedSections = useMemo(
		() => available.filter((section) => selectedSet.has(section.id)),
		[available, selectedSet],
	)

	if (available.length === 0) return null

	// Nothing selected is the default and means the agent searches every
	// connector, so clearing the last one is a valid state.
	const toggle = (id: string) => {
		setEnabledMcpSectionIds(nextMcpSelection(selectedIds, id))
	}

	const label = mcpTriggerLabel(
		selectedSections.map((section) => section.title),
	)

	return (
		<Popover
			open={open}
			onOpenChange={(next) => {
				setOpen(next)
				if (!next) setSearch("")
			}}
			align="start"
			sideOffset={6}
			className="w-60 p-0"
			content={
				<Command shouldFilter={available.length >= SEARCH_THRESHOLD}>
					{available.length >= SEARCH_THRESHOLD && (
						<CommandInput
							value={search}
							onValueChange={setSearch}
							placeholder="Search MCP servers..."
						/>
					)}
					<CommandList className="max-h-64 p-1 pb-1.5">
						<CommandEmpty>No MCP servers found</CommandEmpty>
						{available.map((section) => (
							<CommandItem
								key={section.id}
								value={`${section.title}-${section.id}`}
								onSelect={() => toggle(section.id)}
							>
								<span className="flex w-full items-center gap-2">
									<ConnectorLogo url={section.url} logoUrl={section.logoUrl} />
									<span className="min-w-0 flex-1 truncate">
										{section.title}
									</span>
									{selectedSet.has(section.id) && (
										<Check className="h-4 w-4 flex-none" />
									)}
								</span>
							</CommandItem>
						))}
					</CommandList>
				</Command>
			}
		>
			<Button
				variant="ghost"
				size="sm"
				Icon={selectedSections.length === 0 ? SquaresConnected : undefined}
				iconRight={ChevronDown}
				iconClassName="h-3.5 w-3.5 opacity-60"
				iconRightClassName="h-3 w-3 opacity-50"
				className="max-w-[13rem] cursor-default gap-1.5"
			>
				<span className="flex min-w-0 items-center gap-1.5">
					{selectedSections.length > 0 && (
						<span className="flex flex-none items-center gap-0.5">
							{selectedSections.map((section) => (
								<ConnectorLogo
									key={section.id}
									url={section.url}
									logoUrl={section.logoUrl}
									sizeClassName="h-3.5 w-3.5"
									iconClassName="h-3 w-3"
								/>
							))}
						</span>
					)}
					<span className="truncate">{label}</span>
				</span>
			</Button>
		</Popover>
	)
}
